/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { ActivityLog, CreateActivityLogInput } from '@/types/database'

/**
 * Hook to fetch activity logs for a project or all projects
 * Returns the 50 most recent activity logs, ordered by creation time (newest first)
 * @param projectId - Optional project ID. If not provided, fetches all activity logs for the user
 */
export function useActivityLogs(projectId?: string | null) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['activityLogs', projectId],
    queryFn: async () => {
      if (!user) return []

      let activities: ActivityLog[] = []

      // If projectId is provided, check user's role in the project
      if (projectId) {
        // Get user's role in this project
        const { data: memberData } = await supabase
          .from('project_members')
          .select('role')
          .eq('project_id', projectId)
          .eq('user_id', user.id)
          .maybeSingle()

        // Check if user is owner
        const { data: projectData } = await supabase
          .from('projects')
          .select('owner_user_id')
          .eq('id', projectId)
          .single()

        const isOwner = projectData?.owner_user_id === user.id
        const role = isOwner ? 'owner' : memberData?.role

        // Contributors only see their own activity (or no activity per permissions matrix)
        if (role === 'contributor') {
          // Return only the contributor's own activities
          const { data, error } = await supabase
            .from('activity_logs')
            .select('*')
            .eq('project_id', projectId)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(50)

          if (error) {
            console.error('[useActivityLogs] Error:', error)
            throw error
          }

          activities = (data || []) as ActivityLog[]
        } else {
          // Admin/Editor/Viewer see all project activity
          const { data, error } = await supabase
            .from('activity_logs')
            .select('*')
            .eq('project_id', projectId)
            .order('created_at', { ascending: false })
            .limit(50)

          if (error) {
            console.error('[useActivityLogs] Error:', error)
            throw error
          }

          activities = (data || []) as ActivityLog[]
        }
      } else {
        // When viewing all activities (no specific project), only show user's own activities
        const { data, error } = await supabase
          .from('activity_logs')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(50)

        if (error) {
          console.error('[useActivityLogs] Error:', error)
          throw error
        }

        activities = (data || []) as ActivityLog[]
      }

      // Fetch user profiles for all unique user_ids
      const userIds = [...new Set(activities.map(a => a.user_id))]

      if (userIds.length === 0) {
        return []
      }

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds)

      // Create a map of user_id to full_name
      const profileMap = new Map(
        (profiles || []).map(p => [p.id, p.full_name])
      )

      // Enrich activities with user information
      return activities.map(activity => ({
        ...activity,
        user: {
          full_name: profileMap.get(activity.user_id) || null
        }
      }))
    },
    enabled: !!user,
    staleTime: 0, // Always consider data stale so invalidation triggers refetch
    refetchOnMount: 'always', // Always refetch when component mounts
  })
}

/**
 * Hook to create activity log entries
 * Automatically adds the current user's ID
 */
export function useCreateActivityLog() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (input: Omit<CreateActivityLogInput, 'user_id'>) => {
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('activity_logs')
        .insert({
          ...input,
          user_id: user.id,
        })
        .select()
        .maybeSingle()

      if (error) {
        throw error
      }

      if (!data) {
        throw new Error('Failed to create activity log')
      }

      return data as ActivityLog
    },
    onSuccess: (data) => {
      // Invalidate activity logs for this project to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['activityLogs', data.project_id] })
    },
  })
}

/**
 * Helper function to log activity without using hooks
 * Useful for logging in contexts where hooks can't be used
 */
export async function logActivity(input: Omit<CreateActivityLogInput, 'user_id'>): Promise<void> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      console.warn('[logActivity] No user found, skipping activity log')
      return
    }

    const { error } = await supabase
      .from('activity_logs')
      .insert({
        ...input,
        user_id: user.id
      })
      .select()

    if (error) {
      console.error('[logActivity] Failed:', error)
      // Don't throw - activity logging failures shouldn't break functionality
    }
  } catch (error) {
    console.error('[logActivity] Exception while logging activity:', error)
  }
}
